import AButton from './AButton.js';
import AContainer from './AContainer.js';
import AText from './AText.js';
import Utils from '../Utils.js';
import AImage from './AImage.js';
import ASpine from './ASpine.js';
import AEmitter from './AEmitter.js';
import gameData from '../../gameData.js';
import InputHelper from '../InputHelper.js';

export default class AScene extends Phaser.Scene {
  constructor(sceneName) {
    super(sceneName);

    this.defaultAtlas = 'UIAssets';
    //this.particleAtlas = 'particleAssets';
    this.defaultFont = gameData.fonts.regular;

    this.defaultAudioSprite = 'gameAudio';

    this.buttons = [];
  }

  preload() {
    Utils.CurrentScene = this;

    InputHelper.Listen(InputHelper.KeyCodes.SPACE, () => {
      let interactiveFunc = (children) => {
        children.forEach((child) => {
          if (child instanceof AContainer || child instanceof Phaser.GameObjects.Container) {
            interactiveFunc(child.list);
            return;
          }
          if (child instanceof Phaser.GameObjects.Graphics || child.constructor.name == "SpineGameObject") return;
          this.input.setDraggable(child.setInteractive({ pixelPerfect: true }));
          child.on("drag", (pointer, dragX, dragY) => {
            child.setPosition(dragX, dragY);
            console.log(Phaser.Math.RoundTo(child.x, 0) + ", " + Phaser.Math.RoundTo(child.y, 0));
          });
        });
      };
      interactiveFunc(this.children.getChildren());
    }, 'keydown');
  }

  /**
   * Adds a Mask. YOU CANNOT ADD TO A CHILD
   * customVertices: {x2:, y2:, x3:, y3:, x4:, y4:};
   * @param {object} customVertices 
   * @return {Phaser.GameObjects.Graphics} 
   */
  addSquareMask(child, x, y, width, height, parent = null, customVertices = null, debug = false) {
    let shape = this.createRect(x, y, width, height, 0xff0, parent, customVertices).setVisible(debug);
    let mask = new Phaser.Display.Masks.GeometryMask(this, shape);

    if (child.length) {
      for (var i = 0; i < child.length; i++) {
        child[i].mask = mask;
      }
    } else {
      child.mask = mask;
    }

    return shape;
  }

  /**
   * Adds a Mask. YOU CANNOT ADD TO A CHILD
   * @return {Phaser.GameObjects.Graphics} 
   */
  addTriangularMask(child, x1, y1, x2, y2, x3, y3, debug = false) {
    let shape = this.createTriangle(x1, y1, x2, y2, x3, y3, 0xff0).setVisible(debug);
    let mask = new Phaser.Display.Masks.GeometryMask(this, shape);

    if (child.length) {
      for (var i = 0; i < child.length; i++) {
        child[i].mask = mask;
      }
    } else {
      child.mask = mask;
    }

    return shape;
  }

  /**
   * @return {ASpine} 
   */
  addSpine(x, y, key, animation, loop = false, onComplete = null, skin = null) {
    return new ASpine(this, x, y, key, animation, loop, onComplete, skin);
  }

  /**
   * Adds a Mask. YOU CANNOT ADD TO A CHILD
   * @return {Phaser.GameObjects.Graphics} 
   */
  addPathMask(child, x, y, points) {
    let poly = new Phaser.Geom.Polygon(points);

    let graphics = this.make.graphics();
    graphics.points = poly.points;
    graphics.x = x;
    graphics.y = y;

    graphics.fillStyle(0xff0000);
    graphics.fillPoints(poly.points);
    graphics.fillPath();
    graphics.save();
    graphics.restore();

    child.mask = new Phaser.Display.Masks.BitmapMask(this, graphics);

    return graphics;
  }

  /**
   * config: scaleOnDown, tintOnDown, frameOnDown
   * @return {AButton}
   */
  addButton(x, y, frameUp, config, onClick, parent = null, atlas = this.defaultAtlas) {
    let button = new AButton(this, x, y, atlas, frameUp, config, onClick);
    this.addToParent(button, parent);
    this.buttons.push(button);
    return button;
  }

  removeButton(button) {
    this.buttons.splice(this.buttons.indexOf(button), 1);
  }

  /**
   * @param {AContainer} container 
   * @param {AContainer} parent 
   * @return {AContainer}
   */
  addContainer(x, y, container, parent = null) {
    if (container == null) container = new Phaser.GameObjects.Container(this, 0, 0);
    let cont = container;
    cont.setPosition(x, y);
    this.addToParent(cont, parent);
    return cont;
  }

  /** 
   * CONFIG: {width, height}
   * @param {object} config 
   * @return {AText}
   */
  addText(x, y, textSize, hexColor, font, text = "", parent = null, alignMiddle = true, wordWrapWidth) {
    let textField = new AText(this, x, y, textSize, hexColor, font, text, wordWrapWidth);
    if (alignMiddle) {
      textField.setOrigin(.5);
      textField.setAlign('center');
    }
    this.addToParent(textField, parent);
    return textField;
  }

  /** 
   * @return {AImage}
   */
  addImage(x, y, key, atlas = this.defaultAtlas, parent = null) {
    let image = new AImage(this, x, y, atlas, key, null);
    this.addToParent(image, parent);
    return image;
  }

  /**
   * @returns {AEmitter}
   */
  addEmitter(properties, frame = null, atlas = null, parent = null) {
    //atlas == null ? scene.defaultAtlas : atlas;
    //if(anim) this.createAnimatedParticle(atlas);
    let emitter = new AEmitter(this, properties, frame, atlas);
    this.addToParent(emitter.manager, parent);
    emitter.scaleX.onUpdate = emitter.scaleX.defaultUpdate;
    return emitter;

  }

  addDelay(delay, func) {
    return this.time.delayedCall(delay, func);
  }

  /**
   * @param {Phaser.Math.Easing} ease 
   * @param {Phaser.GameObjects.GameObject} object 
   * @return {Phaser.Tweens.Tween}
   */
  addTween(object, duration, parameters, ease = null, onComplete = null) {
    if (object == null) return null;

    parameters.targets = object;
    parameters.ease = ease;
    parameters.duration = duration;
    parameters.onComplete = onComplete;

    if (parameters.onStart && parameters.delay) {
      let startFunc = parameters.onStart;
      delete parameters.onStart;
      this.addDelay(parameters.delay, startFunc);
    }

    if (parameters.tint != null) {
      this.tweenTint(object, parameters.tint, duration);
      delete parameters.tint;
    }
    let tween = this.tweens.add(parameters);

    return tween;
  }

  killTween(object) {
    this.tweens.killTweensOf(object);
  }

  /**
   * @return {Phaser.GameObjects.Graphics} 
   */
  createCirc(x, y, radius, color, parent = null) {
    let circ = this.add.graphics({ fillStyle: { color: color } });
    circ.fillCircleShape(new Phaser.Geom.Circle(x, y, radius));
    this.addToParent(circ, parent);
    return circ;
  }

  /**
   * @return {Phaser.GameObjects.Graphics} 
   */
  createRect(x, y, width, height, color, parent = null, customRect = null) {
    let rec = this.add.graphics({ fillStyle: { color: color } });
    rec.displayOriginX = width * .5;
    rec.displayOriginY = height * .5;
    this.addToParent(rec, parent);

    if (customRect) {
      rec.beginPath();
      rec.moveTo(x, y);
      rec.lineTo(customRect.x2, customRect.y2);
      rec.lineTo(customRect.x3, customRect.y3);
      rec.lineTo(customRect.x4, customRect.y4);
      rec.closePath();
      rec.fillPath();
    } else {
      rec.fillRectShape(new Phaser.Geom.Rectangle(x, y, width, height));
    }

    return rec;
  }

  /**
   * @return {Phaser.GameObjects.Graphics} 
   */
  createTriangle(x1, y1, x2, y2, x3, y3, color, parent = null) {
    let tri = this.add.graphics({ fillStyle: { color: color } });
    this.addToParent(tri, parent);
    tri.fillTriangleShape(new Phaser.Geom.Triangle(x1, y1, x2, y2, x3, y3));

    return tri;
  }

  addToParent(object, parent) {
    if (!parent || parent == this) this.add.existing(object);
    else parent.add(object);
  }

  get size() {
    return { width: this.game.config.width, height: this.game.config.height };
  }

  get mid() {
    return { x: this.game.config.width * .5, y: this.game.config.height * .5 };
  }

  /**
   * @param {AImage} image 
   * @param {Phaser.Display.Color} endColor 
   */
  tweenTint(image, endColor, duration) {
    let startColor = Phaser.Display.Color.IntegerToColor(image.tint);
    endColor = Phaser.Display.Color.IntegerToColor(endColor);
    let tween = this.tweens.addCounter({
      from: 0, to: 100, duration: duration, onUpdate: () => {
        let col = Phaser.Display.Color.Interpolate.ColorWithColor(startColor, endColor, 100, tween.getValue());
        image.tint = Phaser.Display.Color.ObjectToColor(col).color;
      }
    });
  }

  /**
   * @return {Phaser.Sound.HTML5AudioSound}
   */
  playSound(sound, volume = 1, repeat = false, audioSprite = this.defaultAudioSprite) {
    if (!gameData.gameMuted) {
      let gameAudio = this.sound.addAudioSprite(audioSprite);

      gameAudio.play(sound);
      gameAudio.volume = volume;
      gameAudio.loop = repeat;

      return gameAudio;
    }
  }

  /**
   * @return {Phaser.Sound.HTML5AudioSound}
   */
  playModeSound(sound) {
    let gameAudio;
    if (gameData.gameMode === "rhy") gameAudio = this.sound.addAudioSprite(this.rhyAudioSprite);
    if (gameData.gameMode === "alli") gameAudio = this.sound.addAudioSprite(this.alliAudioSprite);
    if (gameData.gameMode === "syl") gameAudio = this.sound.addAudioSprite(this.sylAudioSprite);


    gameAudio.play(sound);
    gameAudio.volume = 1;
    gameAudio.loop = false;

    return gameAudio;
  }

  /**
   * @return {Phaser.Utils.Array} 
   */
  shuffleArray(value) {
    for (var i = value.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var temp = value[i];
      value[i] = value[j];
      value[j] = temp;
    }
    return value;
  }

  update() {
    // for(let i = 0; i < this.buttons.length; i++)
    // {
    //     let button = this.buttons[i];`
    //     button.update();
    // }
  }
}